import React, { useState, useMemo, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ModalMessage from '../ModalMessage/ModalMessage';
import Icon from '../Icon/Icon';
import { getCountriesAndRegions } from '../../Reducers/actions/shippingMethodsActions';
import { DestinationSelectorSkeleton } from '../Skeletons/FinanceListSkeletons';
import './DestinationSelector.scss';

const DestinationSelector = ({ selectedDestinations = [], onSelect, onClose }) => {
    // ALL HOOKS MUST BE CALLED IN THE SAME ORDER - NO CONDITIONALS
    const dispatch = useDispatch();
    const shippingMethodsState = useSelector((state) => state.shippingMethods);
    const countriesRegions = (shippingMethodsState && shippingMethodsState.countriesRegions) || null;
    const countriesRegionsLoading = (shippingMethodsState && shippingMethodsState.countriesRegionsLoading) || false;

    const [searchQuery, setSearchQuery] = useState('');
    const [expandedRegions, setExpandedRegions] = useState(new Set());
    const [expandedSubRegions, setExpandedSubRegions] = useState(new Set());
    const [selected, setSelected] = useState(new Set());

    useEffect(() => {
        if (!countriesRegions && !countriesRegionsLoading) {
            dispatch(getCountriesAndRegions());
        }
    }, [dispatch, countriesRegions, countriesRegionsLoading]);

    // Transform backend data to frontend format
    const transformedData = useMemo(() => {
        if (!countriesRegions) return { regions: [], subRegions: [], countries: [] };

        // Debug: log backend structure to understand what we're receiving (remove after confirming structure)
        if (process.env.NODE_ENV === 'development') {
            console.log('Backend countries-regions data:', countriesRegions);
        }

        const { regions, allCountries, allRegions } = countriesRegions;
        const transformedRegions = [];
        const transformedSubRegions = [];
        const transformedCountries = [];

        // Map to track region IDs for lookup
        const regionIdMap = new Map();

        // Add "Everywhere" option
        transformedRegions.push({
            id: 'everywhere',
            name: 'Everywhere',
            type: 'region'
        });

        // Transform continents/regions - use whatever structure backend provides
        if (regions) {
            // Handle different possible backend structures
            let continentsData = null;

            // Check if regions has a 'continents' property
            if (regions.continents) {
                continentsData = regions.continents;
            }
            // Or if regions itself is the continents object
            else if (typeof regions === 'object' && !Array.isArray(regions)) {
                // Check if it's a flat structure with continent keys
                const possibleContinentKeys = Object.keys(regions).filter(key =>
                    !key.includes('SubRegions') && !key.includes('subRegions') && key !== 'allCountries'
                );
                if (possibleContinentKeys.length > 0) {
                    continentsData = {};
                    possibleContinentKeys.forEach(key => {
                        if (regions[key] && regions[key].name) {
                            continentsData[key] = regions[key];
                        }
                    });
                }
            }

            // Process continents
            if (continentsData) {
                Object.entries(continentsData).forEach(([key, continent]) => {
                    if (continent && continent.name) {
                        transformedRegions.push({
                            id: key,
                            name: continent.name,
                            type: 'region'
                        });
                        regionIdMap.set(key, key);

                        // Add countries from this continent
                        if (continent.countries && Array.isArray(continent.countries)) {
                            continent.countries.forEach(country => {
                                transformedCountries.push({
                                    id: country.code?.toLowerCase() || country.code,
                                    code: country.code,
                                    name: country.name,
                                    type: 'country',
                                    region: key
                                });
                            });
                        }
                    }
                });
            }

            // Process sub-regions dynamically - look for any sub-region structure
            const subRegionKeys = Object.keys(regions).filter(key =>
                key.includes('SubRegions') || key.includes('subRegions') || key.includes('sub-regions')
            );

            subRegionKeys.forEach(subRegionKey => {
                const subRegionsData = regions[subRegionKey];
                if (subRegionsData && typeof subRegionsData === 'object') {
                    // Try to infer parent region from key name (e.g., 'europeanSubRegions' -> 'europe')
                    // This is a fallback - backend should provide parent region in the data
                    let inferredParentRegion = null;
                    const keyLower = subRegionKey.toLowerCase();
                    if (keyLower.includes('europe')) inferredParentRegion = 'europe';
                    else if (keyLower.includes('africa')) inferredParentRegion = 'africa';
                    else if (keyLower.includes('asia')) inferredParentRegion = 'asia';
                    else if (keyLower.includes('america')) inferredParentRegion = 'americas';
                    else if (keyLower.includes('oceania')) inferredParentRegion = 'oceania';

                    Object.entries(subRegionsData).forEach(([key, subRegion]) => {
                        if (subRegion && subRegion.name) {
                            // Use parent region from subRegion data if available (preferred)
                            // Check multiple possible property names
                            const parentRegion = subRegion.region ||
                                subRegion.parentRegion ||
                                subRegion.continent ||
                                subRegion.parent ||
                                inferredParentRegion;

                            // Only add if we have a valid parent region
                            if (parentRegion) {
                                transformedSubRegions.push({
                                    id: key,
                                    name: subRegion.name,
                                    type: 'sub-region',
                                    region: parentRegion
                                });

                                // Add countries from this sub-region
                                if (subRegion.countries && Array.isArray(subRegion.countries)) {
                                    subRegion.countries.forEach(country => {
                                        const existingCountry = transformedCountries.find(c => c.code === country.code);
                                        if (existingCountry) {
                                            existingCountry.subRegion = key;
                                            // Update region if not set
                                            if (!existingCountry.region && parentRegion) {
                                                existingCountry.region = parentRegion;
                                            }
                                        } else {
                                            transformedCountries.push({
                                                id: country.code?.toLowerCase() || country.code,
                                                code: country.code,
                                                name: country.name,
                                                type: 'country',
                                                region: parentRegion,
                                                subRegion: key
                                            });
                                        }
                                    });
                                }
                            }
                        }
                    });
                }
            });
        }

        // Add any remaining countries from allCountries that weren't added yet
        if (allCountries && Array.isArray(allCountries)) {
            allCountries.forEach(country => {
                const existing = transformedCountries.find(c => c.code === country.code);
                if (!existing) {
                    transformedCountries.push({
                        id: country.code?.toLowerCase() || country.code,
                        code: country.code,
                        name: country.name,
                        type: 'country'
                    });
                }
            });
        }

        return {
            regions: transformedRegions,
            subRegions: transformedSubRegions,
            countries: transformedCountries
        };
    }, [countriesRegions]);

    // Initialize selected state based on selectedDestinations
    // This must be after transformedData is defined
    useEffect(() => {
        if (!transformedData.regions.length) return; // Wait for data to be transformed
        
        const initialSelected = new Set();
        
        // First, add all explicitly selected destinations
        if (selectedDestinations && selectedDestinations.length > 0) {
            selectedDestinations.forEach(d => {
                const id = d.id || d.code || d;
                if (id) initialSelected.add(id);
            });
        }

        // Then, check for regions/sub-regions that should be selected
        // because all their children are selected
        
        // Check sub-regions first (they are children of regions)
        // A sub-region is selected if:
        // 1. It's explicitly in selectedDestinations, OR
        // 2. All its countries are selected
        transformedData.subRegions.forEach(subRegion => {
            const subRegionId = subRegion.id;
            // If already explicitly selected, skip
            if (initialSelected.has(subRegionId)) return;
            
            // Get all countries in this sub-region
            const countriesInSubRegion = transformedData.countries.filter(
                c => c.subRegion === subRegionId
            );
            
            // If all countries in this sub-region are selected, mark sub-region as selected
            if (countriesInSubRegion.length > 0) {
                const allCountriesSelected = countriesInSubRegion.every(country => {
                    const countryId = country.id || country.code;
                    return initialSelected.has(countryId);
                });
                if (allCountriesSelected) {
                    initialSelected.add(subRegionId);
                }
            }
        });

        // Check regions (continents)
        // A region is selected if:
        // 1. It's explicitly in selectedDestinations, OR
        // 2. All its direct countries AND all its sub-regions (and their countries) are selected
        transformedData.regions.forEach(region => {
            if (region.id === 'everywhere') return; // Skip "everywhere"
            
            const regionId = region.id;
            // If already explicitly selected, keep it selected (already added above)
            // We still need to check if it should be selected based on children
            
            // Get all countries directly in this region (not in sub-regions)
            const directCountries = transformedData.countries.filter(
                c => c.region === regionId && !c.subRegion
            );
            
            // Get all sub-regions in this region
            const subRegionsInRegion = transformedData.subRegions.filter(
                sr => sr.region === regionId
            );
            
            // Check if all direct countries are selected
            // If there are no direct countries, consider it as "all selected" (since there are none to check)
            const allDirectCountriesSelected = directCountries.length === 0 || 
                directCountries.every(country => {
                    const countryId = country.id || country.code;
                    return initialSelected.has(countryId);
                });
            
            // Check if all sub-regions are selected
            const allSubRegionsSelected = subRegionsInRegion.length > 0 &&
                subRegionsInRegion.every(subRegion => {
                    return initialSelected.has(subRegion.id);
                });
            
            // Also check countries in sub-regions
            let allCountriesInSubRegionsSelected = true;
            if (subRegionsInRegion.length > 0) {
                subRegionsInRegion.forEach(subRegion => {
                    const countriesInSubRegion = transformedData.countries.filter(
                        c => c.subRegion === subRegion.id
                    );
                    const allSelected = countriesInSubRegion.length === 0 || 
                        countriesInSubRegion.every(country => {
                            const countryId = country.id || country.code;
                            return initialSelected.has(countryId);
                        });
                    if (!allSelected) {
                        allCountriesInSubRegionsSelected = false;
                    }
                });
            }
            
            // If all direct countries AND all sub-regions (and their countries) are selected,
            // mark the region as selected (if not already selected)
            const hasDirectCountries = directCountries.length > 0;
            const hasSubRegions = subRegionsInRegion.length > 0;
            
            if (hasDirectCountries && hasSubRegions) {
                // Region has both direct countries and sub-regions
                if (allDirectCountriesSelected && allSubRegionsSelected && allCountriesInSubRegionsSelected) {
                    initialSelected.add(regionId);
                }
            } else if (hasDirectCountries && !hasSubRegions) {
                // Region only has direct countries
                if (allDirectCountriesSelected) {
                    initialSelected.add(regionId);
                }
            } else if (!hasDirectCountries && hasSubRegions) {
                // Region only has sub-regions
                if (allSubRegionsSelected && allCountriesInSubRegionsSelected) {
                    initialSelected.add(regionId);
                }
            }
        });

        setSelected(initialSelected);
    }, [selectedDestinations, transformedData]);

    const allDestinations = useMemo(() => {
        return [...transformedData.regions, ...transformedData.subRegions, ...transformedData.countries];
    }, [transformedData]);

    const filteredDestinations = useMemo(() => {
        if (!searchQuery.trim()) {
            return allDestinations;
        }
        const query = searchQuery.toLowerCase().trim();
        return allDestinations.filter(dest => {
            const name = (dest.name || '').toLowerCase();
            const code = (dest.code || '').toLowerCase();
            // Search in both name and code (partial matching)
            // Check if query is contained in name or code
            const nameMatch = name.includes(query);
            const codeMatch = code.includes(query);
            // Also check if name starts with query for better results
            const nameStartsWith = name.startsWith(query);
            const matches = nameMatch || codeMatch || nameStartsWith;
            return matches;
        });
    }, [searchQuery, allDestinations]);

    // Build hierarchical structure: Region > Sub-region > Country
    const hierarchicalStructure = useMemo(() => {
        const structure = [];

        transformedData.regions.forEach(region => {
            if (region.id === 'everywhere') {
                // Everywhere is special - show it first
                structure.push({
                    region,
                    subRegions: [],
                    directCountries: []
                });
            } else {
                const subRegions = transformedData.subRegions.filter(sr => sr.region === region.id);
                const directCountries = transformedData.countries.filter(c =>
                    c.region === region.id && !c.subRegion
                );
                const subRegionGroups = subRegions.map(subRegion => ({
                    subRegion,
                    countries: transformedData.countries.filter(c => c.subRegion === subRegion.id)
                }));

                structure.push({
                    region,
                    subRegions: subRegionGroups,
                    directCountries
                });
            }
        });

        return structure;
    }, [transformedData]);

    const toggleRegion = (regionId) => {
        setExpandedRegions(prev => {
            const newSet = new Set(prev);
            if (newSet.has(regionId)) {
                newSet.delete(regionId);
            } else {
                newSet.add(regionId);
            }
            return newSet;
        });
    };

    const toggleSubRegion = (subRegionId) => {
        setExpandedSubRegions(prev => {
            const newSet = new Set(prev);
            if (newSet.has(subRegionId)) {
                newSet.delete(subRegionId);
            } else {
                newSet.add(subRegionId);
            }
            return newSet;
        });
    };

    const toggleSelection = (destination) => {
        setSelected(prev => {
            const newSet = new Set(prev);
            const destId = destination.id || destination.code;
            const isCurrentlySelected = newSet.has(destId);

            if (isCurrentlySelected) {
                // Deselect this destination
                newSet.delete(destId);

                // If it's a region, deselect all countries in that region
                if (destination.type === 'region' && destId !== 'everywhere') {
                    transformedData.countries.forEach(country => {
                        if (country.region === destId) {
                            newSet.delete(country.id || country.code);
                        }
                    });
                    // Also deselect sub-regions in this region
                    transformedData.subRegions.forEach(subRegion => {
                        if (subRegion.region === destId) {
                            newSet.delete(subRegion.id);
                        }
                    });
                }

                // If it's a sub-region, deselect all countries in that sub-region
                if (destination.type === 'sub-region') {
                    transformedData.countries.forEach(country => {
                        if (country.subRegion === destId) {
                            newSet.delete(country.id || country.code);
                        }
                    });
                }
            } else {
                // Select this destination
                newSet.add(destId);

                // If it's a region, select all countries in that region
                if (destination.type === 'region') {
                    if (destId === 'everywhere') {
                        // Select all countries
                        transformedData.countries.forEach(country => {
                            newSet.add(country.id || country.code);
                        });
                        // Select all regions and sub-regions
                        transformedData.regions.forEach(region => {
                            if (region.id !== 'everywhere') {
                                newSet.add(region.id);
                            }
                        });
                        transformedData.subRegions.forEach(subRegion => {
                            newSet.add(subRegion.id);
                        });
                    } else {
                        // Select all countries in this region
                        transformedData.countries.forEach(country => {
                            if (country.region === destId) {
                                newSet.add(country.id || country.code);
                            }
                        });
                        // Select all sub-regions in this region
                        transformedData.subRegions.forEach(subRegion => {
                            if (subRegion.region === destId) {
                                newSet.add(subRegion.id);
                            }
                        });
                    }
                }

                // If it's a sub-region, select all countries in that sub-region
                if (destination.type === 'sub-region') {
                    transformedData.countries.forEach(country => {
                        if (country.subRegion === destId) {
                            newSet.add(country.id || country.code);
                        }
                    });
                }
            }

            return newSet;
        });
    };

    const handleConfirm = () => {
        const selectedDests = allDestinations.filter(dest => {
            const destId = dest.id || dest.code;
            return selected.has(destId);
        });
        if (onSelect) {
            onSelect(selectedDests);
        }
        if (onClose) {
            onClose();
        }
    };

    const handleClearSearch = () => {
        setSearchQuery('');
    };

    const isSelected = (id) => selected.has(id);
    const isRegionExpanded = (regionId) => expandedRegions.has(regionId);
    const isSubRegionExpanded = (subRegionId) => expandedSubRegions.has(subRegionId);

    // Filter structure based on search
    const displayStructure = useMemo(() => {
        if (!searchQuery.trim()) {
            return hierarchicalStructure;
        }

        // When searching, show all matching items grouped by their regions
        const matchingItems = filteredDestinations;

        // Get all matching countries first
        const matchingCountries = matchingItems.filter(item => {
            const name = (item.name || '').toLowerCase();
            // A country is identified by having type 'country' OR
            // having a name but not being a region/sub-region/continent
            const isCountry = item.type === 'country' || 
                (!item.type && 
                 item.name && 
                 !item.subRegion && 
                 !name.includes('region') && 
                 !name.includes('continent') &&
                 name !== 'africa' &&
                 name !== 'asia' &&
                 name !== 'europe' &&
                 name !== 'americas' &&
                 name !== 'north america' &&
                 name !== 'south america' &&
                 name !== 'oceania');
            return isCountry;
        });

        // Get all matching sub-regions
        const matchingSubRegions = matchingItems.filter(item => {
            return item.type === 'sub-region';
        });

        // Get all matching regions
        const matchingRegions = matchingItems.filter(item => {
            return item.type === 'region';
        });

        // Build a map of region ID to matching countries
        const regionToCountries = new Map();
        matchingCountries.forEach(country => {
            const regionId = country.region || country.parentRegion || country.continent;
            if (regionId) {
                if (!regionToCountries.has(regionId)) {
                    regionToCountries.set(regionId, []);
                }
                regionToCountries.get(regionId).push(country);
            }
        });

        // Build structure with regions that have matches
        const structure = [];
        transformedData.regions.forEach(region => {
            const countriesInRegion = regionToCountries.get(region.id) || [];
            const subRegionsInRegion = matchingSubRegions.filter(sr => 
                sr.region === region.id || sr.parentRegion === region.id
            );
            const regionMatches = matchingRegions.some(r => r.id === region.id || r.name?.toLowerCase() === region.name?.toLowerCase());

            // Show region if it has matching countries, sub-regions, or if the region itself matches
            if (countriesInRegion.length > 0 || subRegionsInRegion.length > 0 || regionMatches) {
                // Get all sub-regions for this region (not just matching ones) to show matching countries
                const allSubRegionsInRegion = transformedData.subRegions.filter(sr => 
                    sr.region === region.id || sr.parentRegion === region.id
                );
                
                // Separate countries by sub-region
                const directCountries = countriesInRegion.filter(c => !c.subRegion);
                
                // For sub-regions: show matching sub-regions OR sub-regions that contain matching countries
                const subRegionGroups = allSubRegionsInRegion
                    .filter(subRegion => {
                        // Include if sub-region matches OR if it has matching countries
                        const subRegionMatches = matchingSubRegions.some(sr => sr.id === subRegion.id);
                        const hasMatchingCountries = countriesInRegion.some(c => 
                            c.subRegion === subRegion.id || c.parentSubRegion === subRegion.id
                        );
                        return subRegionMatches || hasMatchingCountries;
                    })
                    .map(subRegion => {
                        const countriesInSubRegion = countriesInRegion.filter(c => 
                            c.subRegion === subRegion.id || c.parentSubRegion === subRegion.id
                        );
                        return {
                            subRegion,
                            countries: countriesInSubRegion
                        };
                    });

                structure.push({
                    region,
                    subRegions: subRegionGroups,
                    directCountries: directCountries
                });
            }
        });

        // If no regions match but we have matching countries, show them under "Other"
        if (structure.length === 0 && matchingCountries.length > 0) {
            structure.push({
                region: { id: 'other', name: 'Other', type: 'region' },
                subRegions: [],
                directCountries: matchingCountries
            });
        }

        return structure;
    }, [searchQuery, hierarchicalStructure, filteredDestinations, transformedData]);

    return (
        <ModalMessage onClose={onClose}>
            <div className="destination-selector">
                {countriesRegionsLoading ? (
                    <DestinationSelectorSkeleton />
                ) : (
                    <>
                        <div className="destination-selector-header ta-center">
                            <p className="fs-sm2 fw-bold fg-dark">Select Destinations</p>
                        </div>

                        {/* Search */}
                        <div className="destination-search">
                            <div className="search-page__search-container">
                                <input
                                    type="text"
                                    className="search-page__search-input"
                                    placeholder="Search..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                />
                                <Icon
                                    alias="search_input_icon"
                                    width={20}
                                    stroke="#FF3232"
                                    color="none"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    strokeWidth={2}
                                    className="search-page__search-icon"
                                />

                            </div>
                        </div>

                        {/* Destination List */}
                        <div className="destination-list">
                            {displayStructure.map(({ region, subRegions, directCountries }) => {
                                if (!region) return null;

                                const regionExpanded = isRegionExpanded(region.id);
                                const showContent = regionExpanded || searchQuery;
                                const regionId = region.id || region.code;

                                return (
                                    <div key={regionId} className="destination-group">
                                        {/* Region Row */}
                                        <div
                                            className="destination-item"
                                            style={{ height: '56px', borderBottom: '1px solid #E5E5E5', padding: "16px 8px" }}
                                            onClick={() => toggleRegion(regionId)}
                                        >
                                            <div className="d-flex aic ">
                                                <div className="checkbox-wrapper" style={{ position: 'relative' }}>
                                                    <input
                                                        type="checkbox"
                                                        checked={isSelected(regionId)}
                                                        onChange={(e) => {
                                                            e.stopPropagation();
                                                            toggleSelection(region);
                                                        }}
                                                        onClick={(e) => e.stopPropagation()}
                                                        className="destination-checkbox"
                                                        style={{
                                                            appearance: 'none',
                                                            WebkitAppearance: 'none',
                                                            MozAppearance: 'none',
                                                            accentColor: 'transparent',
                                                            width: '24px',
                                                            height: '24px',
                                                            border: '2px solid #E5E5E5',
                                                            borderRadius: '4px',
                                                            backgroundColor: isSelected(regionId) ? '#FF3232' : '#FFFFFF',
                                                            cursor: 'pointer',
                                                            position: 'relative',
                                                            zIndex: 1
                                                        }}
                                                    />
                                                    {isSelected(regionId) && (
                                                        <div
                                                            style={{
                                                                position: 'absolute',
                                                                left: '50%',
                                                                top: '35%',
                                                                transform: 'translate(-50%, -50%) rotate(45deg)',
                                                                width: '6px',
                                                                height: '12px',
                                                                border: 'solid white',
                                                                borderWidth: '0 2px 2px 0',
                                                                pointerEvents: 'none',
                                                                zIndex: 2
                                                            }}
                                                        />
                                                    )}
                                                </div>
                                                <Icon
                                                    alias={regionExpanded ? 'chevron-down' : 'chevron-right'}
                                                    width={16}
                                                    color="#1C1B1F"
                                                    className="chevron-icon"
                                                />
                                                <span className="fs-regular fw-bold fg-dark ml-8">
                                                    {region.name}
                                                </span>
                                            </div>
                                        </div>

                                        {/* Sub-regions and Countries */}
                                        {showContent && (
                                            <div className="destination-children">
                                                {/* Sub-regions */}
                                                {subRegions.map(({ subRegion, countries }) => {
                                                    const subRegionExpanded = isSubRegionExpanded(subRegion.id);
                                                    const subRegionId = subRegion.id || subRegion.code;
                                                    return (
                                                        <div key={subRegionId} className="destination-sub-group">
                                                            <div
                                                                className="destination-item destination-sub-item c-pointer"
                                                                style={{ height: '56px', borderBottom: '1px solid #E5E5E5', padding: "16px 8px" }}

                                                                onClick={() => toggleSubRegion(subRegionId)}
                                                            >
                                                                <div className="d-flex aic pl-16">
                                                                    <div className="checkbox-wrapper" style={{ position: 'relative' }}>
                                                                        <input
                                                                            type="checkbox"
                                                                            checked={isSelected(subRegionId)}
                                                                            onChange={(e) => {
                                                                                e.stopPropagation();
                                                                                toggleSelection(subRegion);
                                                                            }}
                                                                            onClick={(e) => e.stopPropagation()}
                                                                            className="destination-checkbox"
                                                                            style={{
                                                                                appearance: 'none',
                                                                                WebkitAppearance: 'none',
                                                                                MozAppearance: 'none',
                                                                                accentColor: 'transparent',
                                                                                width: '24px',
                                                                                height: '24px',
                                                                                border: '2px solid #E5E5E5',
                                                                                borderRadius: '4px',
                                                                                backgroundColor: isSelected(subRegionId) ? '#FF3232' : '#FFFFFF',
                                                                                cursor: 'pointer',
                                                                                position: 'relative',
                                                                                zIndex: 1
                                                                            }}
                                                                        />
                                                                        {isSelected(subRegionId) && (
                                                                            <div
                                                                                style={{
                                                                                    position: 'absolute',
                                                                                    left: '50%',
                                                                                    top: '35%',
                                                                                    transform: 'translate(-50%, -50%) rotate(45deg)',
                                                                                    width: '6px',
                                                                                    height: '12px',
                                                                                    border: 'solid white',
                                                                                    borderWidth: '0 2px 2px 0',
                                                                                    pointerEvents: 'none',
                                                                                    zIndex: 2
                                                                                }}
                                                                            />
                                                                        )}
                                                                    </div>
                                                                    <Icon
                                                                        alias={subRegionExpanded ? 'chevron-down' : 'chevron-right'}
                                                                        width={16}
                                                                        color="#1C1B1F"
                                                                        className="chevron-icon"
                                                                    />
                                                                    <span className="fs-xs fg-dark fw-medium ml-8">
                                                                        {subRegion.name}
                                                                    </span>
                                                                </div>
                                                            </div>

                                                            {/* Countries under sub-region */}
                                                            {(subRegionExpanded || searchQuery) && countries.length > 0 && (
                                                                <div className="destination-countries">
                                                                    {countries.map(country => {
                                                                        const countryId = country.id || country.code;
                                                                        return (
                                                                            <div
                                                                                key={countryId}
                                                                                className="destination-item destination-country c-pointer"
                                                                                style={{ height: '56px', borderBottom: '1px solid #E5E5E5',padding:"16px 8px" }}
                                                                                onClick={() => toggleSelection(country)}
                                                                            >
                                                                                <div className="d-flex aic pl-32">
                                                                                    <div className="checkbox-wrapper" style={{ position: 'relative' }}>
                                                                                        <input
                                                                                            type="checkbox"
                                                                                            checked={isSelected(countryId)}
                                                                                            onChange={(e) => {
                                                                                                e.stopPropagation();
                                                                                                toggleSelection(country);
                                                                                            }}
                                                                                            onClick={(e) => e.stopPropagation()}
                                                                                            className="destination-checkbox"
                                                                                            style={{
                                                                                                appearance: 'none',
                                                                                                WebkitAppearance: 'none',
                                                                                                MozAppearance: 'none',
                                                                                                accentColor: 'transparent',
                                                                                                width: '24px',
                                                                                                height: '24px',
                                                                                                border: '2px solid #E5E5E5',
                                                                                                borderRadius: '4px',
                                                                                                backgroundColor: isSelected(countryId) ? '#FF3232' : '#FFFFFF',
                                                                                                cursor: 'pointer',
                                                                                                position: 'relative',
                                                                                                zIndex: 1
                                                                                            }}
                                                                                        />
                                                                                        {isSelected(countryId) && (
                                                                                            <div
                                                                                                style={{
                                                                                                    position: 'absolute',
                                                                                                    left: '50%',
                                                                                                    top: '35%',
                                                                                                    transform: 'translate(-50%, -50%) rotate(45deg)',
                                                                                                    width: '6px',
                                                                                                    height: '12px',
                                                                                                    border: 'solid white',
                                                                                                    borderWidth: '0 2px 2px 0',
                                                                                                    pointerEvents: 'none',
                                                                                                    zIndex: 2
                                                                                                }}
                                                                                            />
                                                                                        )}
                                                                                    </div>
                                                                                    <span className="fs-xs fg-dark fw-medium ml-8">
                                                                                        {country.name}
                                                                                    </span>
                                                                                </div>
                                                                            </div>
                                                                        );
                                                                    })}
                                                                </div>
                                                            )}
                                                        </div>
                                                    );
                                                })}

                                                {/* Direct countries (not in sub-regions) */}
                                                {directCountries.length > 0 && (
                                                    <div className="destination-countries">
                                                        {directCountries.map(country => {
                                                            const countryId = country.id || country.code;
                                                            return (
                                                                <div
                                                                    key={countryId}
                                                                    className="destination-item destination-country c-pointer"
                                                                    style={{ height: '56px', borderBottom: '1px solid #E5E5E5',padding:"16px 8px" }}
                                                                    onClick={() => toggleSelection(country)}
                                                                >
                                                                    <div className="d-flex aic pl-32">
                                                                        <div className="checkbox-wrapper" style={{ position: 'relative' }}>
                                                                            <input
                                                                                type="checkbox"
                                                                                checked={isSelected(countryId)}
                                                                                onChange={(e) => {
                                                                                    e.stopPropagation();
                                                                                    toggleSelection(country);
                                                                                }}
                                                                                onClick={(e) => e.stopPropagation()}
                                                                                className="destination-checkbox"
                                                                                style={{
                                                                                    appearance: 'none',
                                                                                    WebkitAppearance: 'none',
                                                                                    MozAppearance: 'none',
                                                                                    accentColor: 'transparent',
                                                                                    width: '24px',
                                                                                    height: '24px',
                                                                                    border: '2px solid #E5E5E5',
                                                                                    borderRadius: '4px',
                                                                                    backgroundColor: isSelected(countryId) ? '#FF3232' : '#FFFFFF',
                                                                                    cursor: 'pointer',
                                                                                    position: 'relative',
                                                                                    zIndex: 1
                                                                                }}
                                                                            />
                                                                            {isSelected(countryId) && (
                                                                                <div
                                                                                    style={{
                                                                                        position: 'absolute',
                                                                                        left: '50%',
                                                                                        top: '35%',
                                                                                        transform: 'translate(-50%, -50%) rotate(45deg)',
                                                                                        width: '6px',
                                                                                        height: '12px',
                                                                                        border: 'solid white',
                                                                                        borderWidth: '0 2px 2px 0',
                                                                                        pointerEvents: 'none',
                                                                                        zIndex: 2
                                                                                    }}
                                                                                />
                                                                            )}
                                                                        </div>
                                                                        <span className="fs-xs fg-dark fw-medium ml-8">
                                                                            {country.name}
                                                                        </span>
                                                                    </div>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}

                            {/* Show message when searching and no results */}
                            {searchQuery && displayStructure.length === 0 && (
                                <div className="no-results">
                                    <p className="fs-xs fg-dark fw-medium">No results found</p>
                                </div>
                            )}
                        </div>

                        {/* Actions */}
                        <div className="destination-selector-actions">
                            <button
                                className="btn red-btn btn-full"
                                onClick={handleConfirm}
                                style={{ height: '64px' }}
                            >
                                CONFIRM
                            </button>
                            <p
                                className="fs-xxs fg-dark fw-bold mb-16 mt-16 ta-center c-pointer td-underline"
                                onClick={onClose}
                            >
                                CANCEL
                            </p>
                        </div>
                    </>
                )}
            </div>
        </ModalMessage>
    );
};

export default DestinationSelector;
